import React, { useEffect } from 'react';
import { Checkbox, ListItemText, Menu, MenuItem } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { FilterBtn } from '../FilterBtn';

/**
 * FilterByStatus
 *
 * A component that renders a filter button and a dropdown menu to filter items by status.
 *
 * @param {string} text - Text for the filter button.
//  * @param {Array} statusList - List of status to display in the dropdown, each with an `id`, `name`, and `checked` property.
 * @param {function} checkedHandler - Function to handle checkbox changes for each status.
 * @param {object} btnSx - Custom styles for the filter button.
 * @param {object} menuSx - Custom styles for the dropdown menu.
 *
 * @returns {JSX.Element} - A filter button with an attached dropdown menu for selecting companies.
 */

function FilterByStatus({ text = 'Filter by status', onChange = () => {}, selectedStatusList = [], btnSx = {}, menuSx = {} }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [statusList, setStatusList] = React.useState([
        {
            id: '1',
            status: 'Completed',
            checked: false,
        },
        {
            id: '3',
            status: 'Not started',
            checked: false,
        },
    ]);
    const open = Boolean(anchorEl);

    const navigate = useNavigate();
    const location = useLocation();

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = (value) => {
        setAnchorEl(null);
    };

    const checkedHandler = (checked, newTaskStatus) => {
        const newFilteredStatusList = statusList.map((cItem) => {
            if (cItem.id === newTaskStatus.id) {
                const newValue = {
                    ...cItem,
                    checked: checked,
                };
                return newValue;
            }
            return cItem;
        });

        const selectedStatusList = newFilteredStatusList.filter((cItem) => cItem.checked);

        // Create URSearchParams
        const searchParams = new URLSearchParams(location.search);
        const filters = searchParams.getAll('status'); // Returns an array

        if (filters.find((cStatusId) => cStatusId === newTaskStatus.id)) {
            searchParams.delete('status', newTaskStatus.id || '');
            navigate(`${location.pathname}?${searchParams.toString()}`);
        } else {
            searchParams.append('status', newTaskStatus.id || '');
            navigate(`${location.pathname}?${searchParams.toString()}`);
        }

        setStatusList(newFilteredStatusList);
        onChange(selectedStatusList);
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        // Get all 'filter' values
        const filters = searchParams.getAll('status'); // Returns an array

        // get status list with selected and non selected items
        const updatedList = statusList.map((currentStatus) => {
            if (filters.find((id) => id === currentStatus.id)) {
                const newValue = {
                    ...currentStatus,
                    checked: true,
                };

                return newValue;
            } else {
                const newValue = {
                    ...currentStatus,
                    checked: false,
                };

                return newValue;
            }
        });

        const selectedStatusList = updatedList.filter((cItem) => cItem.checked); // get status list with selected items

        setStatusList(updatedList);
        onChange(selectedStatusList);
    }, [location.search]);

    useEffect(() => {
        // get status list with selected and non selected items
        const updatedList = statusList.map((currentStatus) => {
            if (selectedStatusList.find((c) => c.id === currentStatus.id)) {
                const newValue = {
                    ...currentStatus,
                    checked: true,
                };

                return newValue;
            } else {
                const newValue = {
                    ...currentStatus,
                    checked: false,
                };

                return newValue;
            }
        });

        setStatusList(updatedList);
    }, [selectedStatusList]);

    return (
        <>
            <FilterBtn text={text} handleClick={handleClick} sx={{ ...btnSx }} />
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                sx={{
                    '& .MuiPaper-root': {
                        // width: anchorEl?.offsetWidth || '165px',
                    },

                    ...menuSx,
                }}
            >
                {statusList.map((currentStatus) => (
                    <MenuItem
                        key={currentStatus.id}
                        onClick={() => checkedHandler(!currentStatus.checked, currentStatus)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                checkedHandler(!currentStatus.checked, currentStatus);
                            }
                        }}
                    >
                        <Checkbox checked={currentStatus.checked} onChange={(event) => checkedHandler(event.target.checked, currentStatus)} />
                        <ListItemText primary={currentStatus.status} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default FilterByStatus;
